import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import * as UserFileActions from 'src/app/store/user-file/user-file.actions';
import { AppState } from 'src/app/store/app.reducer';
import { TranslateService } from '@ngx-translate/core';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { FinalUsersList } from 'src/app/model/final-users-list';
import { filter, Observable, Subscription } from 'rxjs';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { FINAL_USERS_LIST_DEFAULT_SORT_COLUMN, FINAL_USERS_LIST_DEFAULT_SORT_COLUMN_DIRECTION, FINAL_USERS_LIST_PAGE_DEFAULT_SIZE, FINAL_USERS_LIST_PAGE_SIZE_OPTIONS } from 'src/app/conf/final-users-list';
import { Device } from 'src/app/model/device';
import { selectCurrentDevice } from 'src/app/store/device/device.selector';
import { selectDeletedFinalUsersList, selectDeletingFinalUsersList, selectFinalUsersListEdited, selectFinalUsersListEditing, selectFinalUsersListExported, selectFinalUsersListLoading, selectFinalUsersLists, selectFinalUsersPaging } from 'src/app/store/user-file/user-file.selector';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { EditFinalUsersDialogComponent } from '../edit-final-users-dialog/edit-final-users-dialog.component';
import { ImportFinalUsersDialogComponent } from '../import-final-users-dialog/import-final-users-dialog.component';
import { selectIsImpersMode, selectUser } from 'src/app/store/user/user.selector';
import { User } from 'src/app/model/user';


@Component({
	selector: 'app-import-final-users',
	templateUrl: './import-final-users.component.html',
	providers: [ConfirmationService, DialogService]
})

export class ImportFinalUsersComponent implements OnInit, OnDestroy {
	currentDevice: Device;
	currentDevice$: Observable<Device | undefined>;
	currentDeviceSub: Subscription | undefined;

	finalUsersLists: FinalUsersList[];
	finalUsersLists$: Observable<FinalUsersList[] | undefined>;
	finalUsersListsSub: Subscription | undefined;

	finalUsersPaging$: Observable<ItemsPagination | undefined>;
	finalUsersPagingSub: Subscription | undefined;

	finalUsersLoading: boolean;
	finalUsersLoading$: Observable<boolean | undefined>;
	finalUsersLoadingSub: Subscription | undefined;

	currentUser: User;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;

	isImpersMode: boolean;
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;

	edited = false;
	edited$: Observable<boolean | undefined>;
	editedSub: Subscription;

	editing = false;
	editing$: Observable<boolean | undefined>;
	editingSub: Subscription;

	deleted = false;
	deleted$: Observable<boolean | undefined>;
	deletedSub: Subscription;

	deleting = false;
	deleting$: Observable<boolean | undefined>;
	deletingSub: Subscription;

	exported = false;
	exported$: Observable<boolean | undefined>;
	exportedSub: Subscription;

	pageSortSub: Subscription | undefined;
	paging: PaginationParams = {
		size: FINAL_USERS_LIST_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: FINAL_USERS_LIST_DEFAULT_SORT_COLUMN,
		order: FINAL_USERS_LIST_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSizeOptions = FINAL_USERS_LIST_PAGE_SIZE_OPTIONS;
	pageSize = FINAL_USERS_LIST_PAGE_DEFAULT_SIZE;
	resultLength: number;

	totalRecords = 0;

	sort: any;

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
		public confirmationService: ConfirmationService
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.currentDevice$ = this.store.select(selectCurrentDevice);
		this.finalUsersLists$ = this.store.select(selectFinalUsersLists);
		this.finalUsersPaging$ = this.store.select(selectFinalUsersPaging);
		this.finalUsersLoading$ = this.store.select(selectFinalUsersListLoading);
		this.edited$ = this.store.select(selectFinalUsersListEdited);
		this.exported$ = this.store.select(selectFinalUsersListExported);
		this.editing$ = this.store.select(selectFinalUsersListEditing);
		this.deleted$ = this.store.select(selectDeletedFinalUsersList);
		this.deleting$ = this.store.select(selectDeletingFinalUsersList);
		this.currentUser$ = this.store.select(selectUser);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
	}

	ngOnInit(): void {
		this.subscribeAll();

		this.getFinalUsersLists();

		this.onResize();
	}

	subscribeAll(): void {
		this.currentDeviceSub = this.currentDevice$?.subscribe(
			(currentDevice) => {
				this.currentDevice = currentDevice;
			}
		);
		this.finalUsersListsSub = this.finalUsersLists$?.subscribe(
			(finalUsersLists) => {
				this.finalUsersLists = finalUsersLists;
			}
		);
		this.finalUsersPagingSub = this.finalUsersPaging$?.pipe(filter(p => !!p))
			.subscribe(
				paging => {
					if (paging !== null && paging !== undefined) {
						this.totalRecords = paging.TotalCount;
					} else {
						this.totalRecords = 0;
					}
				}
			);
		this.finalUsersLoadingSub = this.finalUsersLoading$?.subscribe(
			(loading) => {
				this.finalUsersLoading = loading;
			}
		);
		this.editedSub = this.edited$?.subscribe(
			(edited) => {
				if (edited) {
					this.getFinalUsersLists();
				}
			}
		);
		this.exportedSub = this.exported$?.subscribe(
			(exported) => {
				if (exported) {
					this.getFinalUsersLists();
				}
			}
		);
		this.editingSub = this.editing$?.subscribe(
			(editing) => {
				this.editing = editing;
			}
		);
		this.deletedSub = this.deleted$?.subscribe(
			(deleted) => {
				if (deleted) {
					this.getFinalUsersLists();
				}
			}
		);
		this.deletingSub = this.deleting$?.subscribe(
			(deleting) => {
				this.deleting = deleting;
			}
		);
		this.currentUserSub = this.currentUser$?.subscribe(currentUser => {
			this.currentUser = currentUser;
		});
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);
	}

	ngOnDestroy(): void {
		this.currentDeviceSub?.unsubscribe();
		this.finalUsersListsSub?.unsubscribe();
		this.finalUsersPagingSub?.unsubscribe();
		this.pageSortSub?.unsubscribe();
		this.finalUsersLoadingSub?.unsubscribe();
		this.exportedSub?.unsubscribe();
		this.editingSub?.unsubscribe();
		this.deletedSub?.unsubscribe();
		this.deletingSub?.unsubscribe();
		this.currentUserSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
	}

	loadFiles(event: TableLazyLoadEvent) {
		this.paging = pageSortChange(this.paging, event);

		this.getFinalUsersLists();
	}

	getFinalUsersLists(): void {
		if (this.isImpersMode) {
			this.store.dispatch(UserFileActions.getFinalUsersLists({ paging: this.paging, userId: this.currentUser.Id }));
		} else {
			this.store.dispatch(UserFileActions.getFinalUsersLists({ paging: this.paging }));
		}
	}

	importList(finalUsersLists: FinalUsersList) {
		// this.store.dispatch(UserFileActions.resetFinalUsersListDialog()); / resetImportFinalUsersDialog dipende cosa dobbiamo azzerare
		this.ref = this.dialogService.open(ImportFinalUsersDialogComponent, {
			header: this.translate.instant('IMPORT_LIST'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				finalUsersLists: finalUsersLists,
			}
		});
	}

	editList(list: FinalUsersList) {
		this.store.dispatch(UserFileActions.resetFinalUsersListDialog());
		this.ref = this.dialogService.open(EditFinalUsersDialogComponent, {
			header: this.translate.instant('EDIT_NAME') + ' ' + this.translate.instant('LIST').toLowerCase(),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				list: list
			}
		});
	}

	deleteList(list: FinalUsersList) {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_LIST'),
			message: this.translate.instant('DELETE_LIST_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				if (this.isImpersMode) {
					this.store.dispatch(UserFileActions.deleteFinalUsersList({
						exportList: list, userId: this.currentUser.Id
					}));
				} else {
					this.store.dispatch(UserFileActions.deleteFinalUsersList({
						exportList: list, userId: this.currentUser.Id
					}));
				}
			},
			reject: () => {
			}
		});
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}
}
